/**
 * AnswerButton - Shared Answer Button Component
 *
 * Single responsibility: Rendering yes/no answer buttons for prop questions.
 * Extracted from PropPick for reusability.
 */

import React from 'react';
import { LockBadge } from './LockBadge';
import { getPickOptionTextClass, getPickResultSurfaceClass, hasPickResult } from './pickResultStyles';
import {
  getPickLockedBackgroundClass,
  getPickLockedBorderClass,
  getPickLockedLabelClass,
} from './pickLockedStyles';

interface AnswerButtonProps {
  answer: 'yes' | 'no' | 'over' | 'under';
  label: string;
  isSelected: boolean;
  percentage?: number;
  onClick: () => void;
  disabled?: boolean;
  /** Contest locked — muted selected chrome + lock badge. */
  locked?: boolean;
  isCorrect?: boolean | null;
  testId?: string;
  thirdImage?: string;
  isDailyDouble?: boolean;
  questionType?: 'prop' | 'over_under';
  className?: string;
}

export const AnswerButton: React.FC<AnswerButtonProps> = ({
  answer,
  label,
  isSelected,
  percentage,
  onClick,
  disabled = false,
  locked = false,
  isCorrect,
  testId,
  thirdImage,
  questionType: _questionType,
  className = '',
}) => {
  const hasResult = hasPickResult(isCorrect);
  const resultSurfaceClass = getPickResultSurfaceClass(isSelected, isCorrect);
  const labelClass =
    locked && isSelected
      ? getPickLockedLabelClass()
      : getPickOptionTextClass(isSelected, hasResult);
  const showLockBadge = locked && isSelected && !hasResult;

  const getBorderClass = () => {
    if (resultSurfaceClass) return resultSurfaceClass;
    if (locked) return getPickLockedBorderClass(isSelected);
    if (isSelected) return 'border border-white';
    return 'border border-border';
  };

  const getHoverClass = () => {
    if (disabled || hasResult || locked) return '';
    return 'hover:border-muted-foreground';
  };

  const getBackgroundClass = () => {
    if (resultSurfaceClass) return '';
    if (locked) return getPickLockedBackgroundClass(isSelected);
    if (isSelected) return 'bg-white';
    return 'bg-surface-muted';
  };

  return (
    <button
      type="button"
      data-testid={testId || `prop-answer-${answer}`}
      data-test-result={isSelected && hasResult ? (isCorrect ? 'correct' : 'incorrect') : undefined}
      data-selected={isSelected ? 'true' : 'false'}
      data-locked={locked ? 'true' : undefined}
      onClick={() => {
        if (!disabled && !locked) onClick();
      }}
      disabled={disabled || locked}
      className={`
        group peer w-full p-4 rounded-xl transition-smooth flex items-center justify-center relative
        ${getBackgroundClass()}
        ${getBorderClass()}
        ${getHoverClass()}
        ${disabled || locked ? 'cursor-not-allowed' : ''}
        ${disabled && !hasResult && !locked ? 'opacity-90' : ''}
        ${className}
      `}
    >
      {thirdImage && (
        <div className="absolute top-2 right-2 w-10 h-10">
          <img src={thirdImage} alt="Secondary logo" className="w-full h-full object-contain" />
        </div>
      )}

      {percentage !== undefined && (
        <div
          className={`absolute ${thirdImage ? 'top-2 left-2' : 'top-2 right-2'} flex items-center gap-0.5 px-1 py-0.5 rounded bg-black/30`}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-2 h-2"
          >
            <circle cx="12" cy="8" r="4" />
            <path d="M20 21a8 8 0 1 0-16 0" />
          </svg>
          <span className="text-white text-xs font-medium">{percentage}%</span>
        </div>
      )}

      <div
        className={`font-sans font-semibold capitalize ${
          label.split(' ').some(l => l.length > 14) ? 'text-md' : 'text-lg'
        } ${labelClass}`}
      >
        {label}
      </div>

      {showLockBadge && (
        <LockBadge
          data-testid={`lock-badge-${answer}`}
          className="absolute bottom-2 left-1/2 -translate-x-1/2"
        />
      )}
    </button>
  );
};
